import { pickTimelineItemAtPoint } from './timeline-picker';
import { TimelinePickResult, TimelineRenderEngine, TimelineRenderFrame, TimelineStyleToken } from './types';

type RGBA = [number, number, number, number];

const CIRCLE_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_position;
uniform vec2 u_resolution;
uniform vec2 u_center;
uniform float u_radius;
out vec2 v_coord;
void main() {
  vec2 world = u_center + a_position * u_radius;
  vec2 clip = (world / u_resolution) * 2.0 - 1.0;
  clip.y = -clip.y;
  gl_Position = vec4(clip, 0.0, 1.0);
  v_coord = a_position;
}
`;

const CIRCLE_FRAGMENT_SHADER = `#version 300 es
precision mediump float;
in vec2 v_coord;
uniform vec4 u_fillColor;
uniform vec4 u_borderColor;
uniform float u_borderWidth;
out vec4 fragColor;
void main() {
  float dist = length(v_coord);
  if (dist > 1.0) {
    discard;
  }
  float borderThreshold = 1.0 - u_borderWidth;
  if (dist > borderThreshold) {
    fragColor = u_borderColor;
  } else {
    fragColor = u_fillColor;
  }
}
`;

const toCssRgba = ([r, g, b, a]: RGBA): string => (
  `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`
);

const clamp = (value: number): number => Math.max(0, Math.min(1, value));

const withAlpha = (color: RGBA, alpha: number): RGBA => [color[0], color[1], color[2], clamp(alpha)];

const mix = (from: RGBA, to: RGBA, ratio: number): RGBA => {
  const w = clamp(ratio);
  const inverse = 1 - w;
  return [
    (from[0] * inverse) + (to[0] * w),
    (from[1] * inverse) + (to[1] * w),
    (from[2] * inverse) + (to[2] * w),
    (from[3] * inverse) + (to[3] * w),
  ];
};

const lighten = (color: RGBA, ratio: number): RGBA => mix(color, [1, 1, 1, color[3]], ratio);

const styleColorsFromBase = (
  style: TimelineStyleToken,
  fillColor: RGBA,
  borderColor: RGBA,
): { fill: RGBA; border: RGBA } => {
  switch (style) {
    case 'hovered':
      return {
        fill: withAlpha(lighten(fillColor, 0.1), 1),
        border: withAlpha(lighten(borderColor, 0.08), 1),
      };
    case 'selected':
      return {
        fill: withAlpha(lighten(fillColor, 0.06), 1),
        border: [0.635, 0.349, 1, 1],
      };
    case 'preview':
      return {
        fill: withAlpha(lighten(fillColor, 0.08), 0.32),
        border: withAlpha(lighten(borderColor, 0.08), 1),
      };
    case 'preview-added':
      return {
        fill: withAlpha(lighten(fillColor, 0.06), 0.96),
        border: mix(borderColor, [0.047, 0.549, 0.914, 1], 0.55),
      };
    case 'preview-muted':
      return {
        fill: withAlpha(lighten(fillColor, 0.1), 0.3),
        border: withAlpha(lighten(borderColor, 0.06), 0.42),
      };
    case 'editing':
      return {
        fill: mix(fillColor, [0.06, 0.72, 0.72, 1], 0.32),
        border: mix(borderColor, [0.04, 0.57, 0.57, 1], 0.4),
      };
    case 'moving':
      return {
        fill: withAlpha(lighten(fillColor, 0.14), 0.95),
        border: [0.635, 0.349, 1, 0.9],
      };
    case 'resizing':
      return {
        fill: mix(fillColor, [0.98, 0.68, 0.16, 1], 0.35),
        border: mix(borderColor, [1, 0.85, 0.45, 1], 0.4),
      };
    case 'normal':
    default:
      return {
        fill: withAlpha(fillColor, Math.max(fillColor[3], 0.85)),
        border: withAlpha(borderColor, Math.max(borderColor[3], 1)),
      };
  }
};

const drawScissorRect = (
  gl: WebGL2RenderingContext,
  frameHeight: number,
  x: number,
  y: number,
  width: number,
  height: number,
  color: RGBA,
) => {
  if (width <= 0 || height <= 0) {
    return;
  }

  const sx = Math.round(x);
  const syTop = Math.round(y);
  const sw = Math.round(width);
  const sh = Math.round(height);
  if (sw <= 0 || sh <= 0) {
    return;
  }

  const syBottom = frameHeight - (syTop + sh);
  if (syBottom > frameHeight || syBottom + sh < 0) {
    return;
  }

  gl.scissor(sx, syBottom, sw, sh);
  gl.clearColor(color[0], color[1], color[2], color[3]);
  gl.clear(gl.COLOR_BUFFER_BIT);
};

const drawItem = (
  gl: WebGL2RenderingContext,
  frameHeight: number,
  item: TimelineRenderFrame['items'][number],
) => {
  const colors = styleColorsFromBase(item.style, item.fillColor, item.borderColor);
  drawScissorRect(gl, frameHeight, item.x, item.y, item.width, item.height, colors.fill);

  const borderWidth = item.style === 'selected' ? 2 : 1;
  drawScissorRect(gl, frameHeight, item.x, item.y, item.width, borderWidth, colors.border);
  drawScissorRect(
    gl,
    frameHeight,
    item.x,
    item.y + item.height - borderWidth,
    item.width,
    borderWidth,
    colors.border,
  );
  drawScissorRect(gl, frameHeight, item.x, item.y, borderWidth, item.height, colors.border);
  drawScissorRect(
    gl,
    frameHeight,
    item.x + item.width - borderWidth,
    item.y,
    borderWidth,
    item.height,
    colors.border,
  );

  if (item.showResizeHandles && !item.isPoint && item.canResize) {
    const handleColor: RGBA = [1, 1, 1, 0.85];
    drawScissorRect(gl, frameHeight, item.x + 1, item.y + 4, 2, item.height - 8, handleColor);
    drawScissorRect(
      gl,
      frameHeight,
      item.x + item.width - 3,
      item.y + 4,
      2,
      item.height - 8,
      handleColor,
    );
  }
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error('Failed to create shader');
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
};

const createCircleProgram = (gl: WebGL2RenderingContext) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, CIRCLE_VERTEX_SHADER);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, CIRCLE_FRAGMENT_SHADER);
  const program = gl.createProgram()!;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`Program link error: ${gl.getProgramInfoLog(program)}`);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);

  const quadVerts = new Float32Array([
    -1, -1, 1, -1, -1, 1,
    -1, 1, 1, -1, 1, 1,
  ]);
  const vao = gl.createVertexArray()!;
  const vbo = gl.createBuffer()!;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, quadVerts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uniforms: {
      resolution: gl.getUniformLocation(program, 'u_resolution')!,
      center: gl.getUniformLocation(program, 'u_center')!,
      radius: gl.getUniformLocation(program, 'u_radius')!,
      fillColor: gl.getUniformLocation(program, 'u_fillColor')!,
      borderColor: gl.getUniformLocation(program, 'u_borderColor')!,
      borderWidth: gl.getUniformLocation(program, 'u_borderWidth')!,
    },
  };
};

export class WebGLTimelineEngine implements TimelineRenderEngine {
  private canvas: HTMLCanvasElement | null = null;

  private gl: WebGL2RenderingContext | null = null;

  private latestFrame: TimelineRenderFrame | null = null;

  private circleProgram: ReturnType<typeof createCircleProgram> | null = null;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      antialias: true,
      depth: false,
      stencil: false,
      alpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      throw new Error('WebGL2 is required');
    }

    this.gl = gl;
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.SCISSOR_TEST);

    this.circleProgram = createCircleProgram(gl);

    const onContextLost = (event: Event) => {
      event.preventDefault();
    };
    canvas.addEventListener('webglcontextlost', onContextLost);
  }

  resize(width: number, height: number, devicePixelRatio: number): void {
    if (!this.canvas || !this.gl) {
      return;
    }

    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));
    const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
    this.canvas.width = Math.max(1, Math.round(safeWidth * dpr));
    this.canvas.height = Math.max(1, Math.round(safeHeight * dpr));
    this.canvas.style.width = `${safeWidth}px`;
    this.canvas.style.height = `${safeHeight}px`;
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  render(frame: TimelineRenderFrame): void {
    const gl = this.gl;
    if (!gl || !this.canvas) {
      return;
    }

    this.latestFrame = frame;
    const dpr = frame.dpr > 0 ? frame.dpr : 1;
    const pixelWidth = Math.max(1, Math.round(frame.width * dpr));
    const pixelHeight = Math.max(1, Math.round(frame.height * dpr));

    if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
      this.resize(frame.width, frame.height, dpr);
    }
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);

    const frameHeightPx = this.canvas.height;

    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(0.118, 0.118, 0.118, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.SCISSOR_TEST);

    const weekendColor: RGBA = [0.16, 0.16, 0.16, 1];
    frame.weekendStripes.forEach(({ x, width: w }) => {
      drawScissorRect(gl, frameHeightPx, x * dpr, 0, w * dpr, frameHeightPx, weekendColor);
    });

    const gridColor: RGBA = [0.2, 0.2, 0.2, 1];
    frame.gridLinesX.forEach((x) => {
      drawScissorRect(gl, frameHeightPx, x * dpr, 0, 1, frameHeightPx, gridColor);
    });

    frame.gridLinesY.forEach((y) => {
      drawScissorRect(gl, frameHeightPx, 0, y * dpr, this.canvas!.width, 1, gridColor);
    });

    const rectItems: typeof frame.items = [];
    const circleItems: typeof frame.items = [];
    frame.items.forEach((item) => {
      const scaled = {
        ...item,
        x: item.x * dpr,
        y: item.y * dpr,
        width: item.width * dpr,
        height: item.height * dpr,
      };
      if (item.isPoint) {
        circleItems.push(scaled);
      } else {
        rectItems.push(scaled);
      }
    });

    rectItems.forEach((item) => {
      drawItem(gl, frameHeightPx, item);
    });

    if (circleItems.length > 0 && this.circleProgram) {
      gl.disable(gl.SCISSOR_TEST);
      const cp = this.circleProgram;
      gl.useProgram(cp.program);
      gl.bindVertexArray(cp.vao);
      gl.uniform2f(cp.uniforms.resolution, this.canvas!.width, this.canvas!.height);

      circleItems.forEach((item) => {
        const colors = styleColorsFromBase(item.style, item.fillColor, item.borderColor);
        const radius = Math.min(item.width, item.height) / 2;
        const cx = item.x + radius;
        const cy = item.y + radius;
        const borderWidth = (item.style === 'selected' ? 2 : 1.5) / radius;

        gl.uniform2f(cp.uniforms.center, cx, cy);
        gl.uniform1f(cp.uniforms.radius, radius);
        gl.uniform4fv(cp.uniforms.fillColor, colors.fill);
        gl.uniform4fv(cp.uniforms.borderColor, colors.border);
        gl.uniform1f(cp.uniforms.borderWidth, borderWidth);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      });

      gl.bindVertexArray(null);
      gl.useProgram(null);
      gl.enable(gl.SCISSOR_TEST);
    }

    if (frame.todayMarkerX !== null) {
      const todayX = frame.todayMarkerX * dpr;
      drawScissorRect(gl, frameHeightPx, todayX, 0, 1, frameHeightPx, [1, 0.2, 0.2, 0.9]);
    }

  }

  pick(screenX: number, screenY: number): TimelinePickResult {
    if (!this.canvas || !this.latestFrame) {
      return { itemId: null, part: 'none' };
    }

    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { itemId: null, part: 'none' };
    }

    const localX = screenX - rect.left;
    const localY = screenY - rect.top;
    if (localX < 0 || localX > rect.width || localY < 0 || localY > rect.height) {
      return { itemId: null, part: 'none' };
    }

    return pickTimelineItemAtPoint(this.latestFrame, localX, localY);
  }

  dispose(): void {
    if (this.circleProgram && this.gl) {
      this.gl.deleteBuffer(this.circleProgram.vbo);
      this.gl.deleteVertexArray(this.circleProgram.vao);
      this.gl.deleteProgram(this.circleProgram.program);
      this.circleProgram = null;
    }

    if (!this.canvas || !this.gl) {
      this.canvas = null;
      this.gl = null;
      this.latestFrame = null;
      return;
    }

    this.canvas.style.background = toCssRgba([0.118, 0.118, 0.118, 1]);
    this.canvas = null;
    this.gl = null;
    this.latestFrame = null;
  }
}
